/**
 * The traffic light. THE rule for this component (WCAG 2.2 AA, and the brief):
 * a signal is never colour-only — it always renders an icon AND a text label,
 * and the three signals use three different glyphs so they stay distinguishable
 * in greyscale, in print and for a colour-blind reader.
 *
 * It takes a `Severity`, which — by the API's schema — only ever exists on a
 * hard `Finding`. Indicative output has no severity at all, so it cannot be
 * passed in here even by mistake.
 */
import { CircleAlert, CircleCheck, TriangleAlert } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { useI18n, type TranslationKey } from '@/i18n'
import { cn } from '@/lib/utils'
import type { Severity } from '@/api/types'

const SIGNALS = {
  red: {
    icon: CircleAlert,
    label: 'severity.red' as TranslationKey,
    meaning: 'severity.red.meaning' as TranslationKey,
    className: 'border-signal-red/30 bg-signal-red-soft text-signal-red-text',
  },
  amber: {
    icon: TriangleAlert,
    label: 'severity.amber' as TranslationKey,
    meaning: 'severity.amber.meaning' as TranslationKey,
    className: 'border-signal-amber/30 bg-signal-amber-soft text-signal-amber-text',
  },
  green: {
    icon: CircleCheck,
    label: 'severity.green' as TranslationKey,
    meaning: 'severity.green.meaning' as TranslationKey,
    className: 'border-signal-green/30 bg-signal-green-soft text-signal-green-text',
  },
} as const satisfies Record<Severity, unknown>

export function SignalBadge({
  severity,
  withMeaning = false,
  className,
}: {
  severity: Severity
  withMeaning?: boolean
  className?: string
}) {
  const { t } = useI18n()
  const signal = SIGNALS[severity]

  return (
    <span
      data-severity={severity}
      data-testid={`signal-${severity}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-0.5 text-[12px] leading-5 font-medium',
        signal.className,
        className,
      )}
    >
      <Icon icon={signal.icon} className="size-3.5" />
      <span>{t(signal.label)}</span>
      {withMeaning ? (
        <span className="font-normal">· {t(signal.meaning)}</span>
      ) : null}
    </span>
  )
}

/** The Ampel counter row. Same rule: icon + label + number, never a bare dot. */
export function SignalCount({ severity, count }: { severity: Severity; count: number }) {
  const { t } = useI18n()
  const signal = SIGNALS[severity]

  return (
    <div
      data-testid={`ampel-${severity}`}
      // `flex-wrap` + `min-w-0`: at 320 CSS px the icon, the number and the two
      // label lines no longer fit on one row and used to push the sheet into a
      // horizontal scroll (WCAG 2.2 §1.4.10). They wrap now — the label never
      // leaves the tile it belongs to.
      className={cn(
        'flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-sm border px-4 py-3',
        signal.className,
      )}
    >
      <Icon icon={signal.icon} className="size-5" />
      <span className="num text-[24px] leading-none font-medium">{count}</span>
      <span className="flex flex-col text-[13px] leading-tight">
        <span className="font-medium">{t(signal.label)}</span>
        <span>{t(signal.meaning)}</span>
      </span>
    </div>
  )
}

export { SIGNALS }
